// ===================================================================
// Intake -- Family Portal (Public)
// Public-facing family lookup page, outside ProtectedRoute
// URL: /portal/:incidentId
// ===================================================================

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, Stack, TextInput,
  Checkbox, Center, ThemeIcon, SimpleGrid, Loader, Box,
  ActionIcon, Tooltip,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconShieldCheck, IconUserCheck,
  IconUserOff, IconRefresh, IconTrash, IconEye,
} from '@tabler/icons-react';
import { TABLES } from '../../lib/api-switch';
import { FIELDS } from '../../api/fields';
import { queryRecords } from '../../api/client';
import { primary } from '../../lib/colors';

// ── Types ────────────────────────────────────────────────────────
interface PortalResult {
  found: boolean;
  displayName: string;
  status: string;
  triage: string;
}

interface WatchEntry {
  firstInitial: string;
  lastName: string;
  dob: string;
  addedAt: string;
}

const WATCH_KEY = 'lifeline-portal-watch';

function getWatchList(): WatchEntry[] {
  try {
    const raw = localStorage.getItem(WATCH_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveWatchList(list: WatchEntry[]) {
  localStorage.setItem(WATCH_KEY, JSON.stringify(list));
}

export default function Portal() {
  const { incidentId } = useParams<{ incidentId: string }>();

  const [firstInitial, setFirstInitial] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [notRobot, setNotRobot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<PortalResult[] | null>(null);
  const [error, setError] = useState('');
  const [watchList, setWatchList] = useState<WatchEntry[]>(getWatchList);

  // ── Search ─────────────────────────────────────────────────────
  const handleSearch = async (
    overrideInitial?: string,
    overrideLastName?: string,
    overrideDob?: string,
  ) => {
    const fi = (overrideInitial ?? firstInitial).trim().toUpperCase();
    const ln = (overrideLastName ?? lastName).trim();
    const d = overrideDob ?? dob;

    if (!fi || !ln || !d) {
      setError('Please fill in all fields: first initial, last name, and date of birth.');
      return;
    }
    if (!notRobot && !overrideInitial) {
      setError('Please confirm you are not a robot.');
      return;
    }
    if (!incidentId) {
      setError('No incident specified in the URL.');
      return;
    }

    setError('');
    setLoading(true);
    setResults(null);

    try {
      // Fetch individuals filtered by incidentId server-side
      const FI = FIELDS.individuals;
      const result = await queryRecords<Record<string, unknown>>(TABLES.individuals, {
        filters: [{ field: FI.incidentId, operator: 'eq', value: incidentId }],
        limit: 100,
      }, { anonymous: true });
      const raw = result.records ?? [];

      // Normalize a date string to YYYY-MM-DD for comparison
      const normDate = (val: string) => {
        if (!val) return '';
        // Already YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
        // Try parsing as date
        const d2 = new Date(val);
        if (!isNaN(d2.getTime())) {
          return d2.toISOString().slice(0, 10);
        }
        return val;
      };

      const searchDob = normDate(d);

      // Filter client-side by last name, dob, and first initial (incidentId already filtered server-side)
      const matches = raw.filter((row) => {
        const rowLastName = String(row[FI.lastName] ?? '').toLowerCase();
        const rowDob = normDate(String(row[FI.dateOfBirth] ?? ''));
        const rowFirst = String(row[FI.firstName] ?? '').toUpperCase();

        return (
          rowLastName === ln.toLowerCase() &&
          rowDob === searchDob &&
          rowFirst.startsWith(fi.charAt(0))
        );
      });

      if (matches.length === 0) {
        setResults([]);
      } else {
        setResults(
          matches.map((row) => {
            const firstName = String(row[FI.firstName] ?? '');
            const lastNameVal = String(row[FI.lastName] ?? '');
            const status = String(row[FI.status] ?? 'active');
            const triage = String(row[FI.triage] ?? 'var(--color-success)');

            // Privacy-safe: show first name + last initial only
            return {
              found: true,
              displayName: `${firstName} ${lastNameVal.charAt(0)}.`,
              status: status === 'discharged' ? 'Released' : 'On Site',
              triage,
            };
          })
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // ── Watch list ─────────────────────────────────────────────────
  const addToWatch = () => {
    const fi = firstInitial.trim().toUpperCase();
    const ln = lastName.trim();
    if (!fi || !ln || !dob) return;

    const exists = watchList.some(
      w => w.firstInitial === fi && (w.lastName || '').toLowerCase() === ln.toLowerCase() && w.dob === dob
    );
    if (exists) return;

    const updated = [...watchList, { firstInitial: fi, lastName: ln, dob, addedAt: new Date().toISOString() }];
    setWatchList(updated);
    saveWatchList(updated);
  };

  const removeFromWatch = (index: number) => {
    const updated = watchList.filter((_, i) => i !== index);
    setWatchList(updated);
    saveWatchList(updated);
  };

  const searchFromWatch = (entry: WatchEntry) => {
    setFirstInitial(entry.firstInitial);
    setLastName(entry.lastName);
    setDob(entry.dob);
    setNotRobot(true);
    handleSearch(entry.firstInitial, entry.lastName, entry.dob);
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8f9fc 0%, #e8eaf6 100%)',
        padding: '40px 16px',
      }}
    >
      <Center>
        <Stack gap="lg" w="100%" maw={560}>
          {/* Header */}
          <Stack align="center" gap="xs">
            <ThemeIcon size={60} radius="xl" variant="gradient" gradient={{ from: primary[800], to: primary[700] }}>
              <IconShieldCheck size={32} color="white" />
            </ThemeIcon>
            <Title order={2} ta="center" style={{ color: primary[900] }}>
              Family Reunification Portal
            </Title>
            <Text size="sm" c="dimmed" ta="center">
              Search for a loved one who may have been registered at an emergency shelter.
            </Text>
          </Stack>

          {/* Search Form */}
          <Paper p="lg" radius="md" withBorder shadow="sm">
            <Stack gap="md">
              <Text fw={600} size="sm">Search for a Person</Text>

              <SimpleGrid cols={{ base: 2 }}>
                <TextInput
                  label="First Initial"
                  placeholder="e.g. J"
                  maxLength={1}
                  value={firstInitial}
                  onChange={e => setFirstInitial(e.target.value.toUpperCase())}
                  styles={{ input: { textTransform: 'uppercase', textAlign: 'center', fontSize: 20 } }}
                />
                <TextInput
                  label="Last Name"
                  placeholder="Full last name"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                />
              </SimpleGrid>

              <TextInput
                label="Date of Birth"
                type="date"
                value={dob}
                onChange={e => setDob(e.target.value)}
              />

              <Checkbox
                label="I confirm I am not a robot"
                checked={notRobot}
                onChange={e => setNotRobot(e.currentTarget.checked)}
              />

              {error && (
                <Text size="sm" c="var(--color-error)">{error}</Text>
              )}

              <Button
                fullWidth
                size="md"
                leftSection={loading ? <Loader size={16} color="white" /> : <IconSearch size={16} />}
                onClick={() => handleSearch()}
                disabled={loading}
                gradient={{ from: primary[800], to: primary[700] }}
                variant="gradient"
              >
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </Stack>
          </Paper>

          {/* Results */}
          {results !== null && (
            <Paper p="lg" radius="md" withBorder shadow="sm">
              {results.length === 0 ? (
                <Stack align="center" gap="sm" py="md">
                  <ThemeIcon size={48} radius="xl" variant="light" color="#939598">
                    <IconUserOff size={24} />
                  </ThemeIcon>
                  <Title order={4} c="dimmed">Not Found</Title>
                  <Text size="sm" c="dimmed" ta="center">
                    No matching person was found in the system. This does not necessarily
                    mean they are not safe. They may not have been registered yet.
                  </Text>
                  <Button
                    variant="light"
                    size="xs"
                    onClick={addToWatch}
                    leftSection={<IconEye size={14} />}
                  >
                    Add to Watch List
                  </Button>
                </Stack>
              ) : (
                <Stack gap="md">
                  {results.map((r, idx) => (
                    <Paper key={idx} p="md" radius="sm" withBorder style={{
                      borderLeft: '4px solid #07963F',
                      background: '#f0fdf4',
                    }}>
                      <Group justify="space-between">
                        <Group gap="sm">
                          <ThemeIcon size={36} radius="xl" color="var(--color-success)" variant="light">
                            <IconUserCheck size={20} />
                          </ThemeIcon>
                          <div>
                            <Text fw={600}>Person Accounted For</Text>
                            <Text size="sm" c="dimmed">{r.displayName}</Text>
                          </div>
                        </Group>
                        <Badge
                          size="lg"
                          variant="filled"
                          color={r.status === 'Released' ? '#939598' : 'var(--color-success)'}
                        >
                          {r.status}
                        </Badge>
                      </Group>
                    </Paper>
                  ))}
                  <Button
                    variant="light"
                    size="xs"
                    onClick={addToWatch}
                    leftSection={<IconEye size={14} />}
                  >
                    Add to Watch List
                  </Button>
                </Stack>
              )}
            </Paper>
          )}

          {/* Watch List */}
          {watchList.length > 0 && (
            <Paper p="lg" radius="md" withBorder shadow="sm">
              <Text fw={600} size="sm" mb="sm">Watch List</Text>
              <Text size="xs" c="dimmed" mb="sm">
                Saved searches for quick refresh. Data stored locally in your browser.
              </Text>
              <Stack gap="xs">
                {watchList.map((entry, idx) => (
                  <Paper key={idx} p="sm" radius="sm" withBorder>
                    <Group justify="space-between">
                      <div>
                        <Text size="sm" fw={500}>
                          {entry.firstInitial}. {entry.lastName}
                        </Text>
                        <Text size="xs" c="dimmed">DOB: {entry.dob ? new Date(entry.dob).toLocaleDateString() : '—'}</Text>
                      </div>
                      <Group gap="xs">
                        <Tooltip label="Search again">
                          <ActionIcon
                            variant="light"
                            color="var(--color-primary-700)"
                            size="sm"
                            onClick={() => searchFromWatch(entry)}
                          >
                            <IconRefresh size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Remove from watch list">
                          <ActionIcon
                            variant="light"
                            color="var(--color-error)"
                            size="sm"
                            onClick={() => removeFromWatch(idx)}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          )}

          {/* Footer */}
          <Text size="xs" c="dimmed" ta="center">
            OpenGov EMC Family Reunification Portal. For privacy, only first name and
            last initial are displayed. Contact your local emergency management agency for
            more information.
          </Text>
        </Stack>
      </Center>
    </Box>
  );
}
